import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Snackbar,
  IconButton
} from '@mui/material';
import {
  Download,
  ShoppingCart,
  Phone,
  Payment,
  CheckCircle,
  Close,
  Search
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import axios from 'axios';
import MobileAppCta from './MobileAppCta';

const ExcelTemplates = ({ onTabChange }) => {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = useState(0);
  const [pollingPurchases, setPollingPurchases] = useState(false);

  const categories = [
    { value: 'all', label: 'All Templates' },
    { value: 'Personal', label: 'Personal' },
    { value: 'Business', label: 'Business' }
  ];

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      
      const categoryParam = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/templates/list${categoryParam}`);
      
      if (response.data && response.data.success) {
        setTemplates(response.data.templates || []);
      } else {
        console.error('Unexpected response format:', response.data);
        showSnackbar('Failed to load templates: Invalid response format', 'error');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
      console.error('Full error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: errorMessage
      });
      
      if (error.response?.status === 404) {
        showSnackbar('Templates endpoint not found. Please ensure the backend is deployed with the latest changes.', 'error');
      } else if (error.response?.status === 500) {
        showSnackbar(`Server error: ${errorMessage}. The database tables may not be set up yet.`, 'error');
      } else {
        showSnackbar(`Failed to load templates: ${errorMessage}`, 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showSnackbar]);

  const fetchMyPurchases = useCallback(async () => {
    if (!isAuthenticated) {
      setMyPurchases([]);
      return;
    }

    try {
      const authHeaders = await getAuthHeaders();
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      
      const response = await axios.get(`${API_URL}/templates/purchases/my`, {
        headers: authHeaders
      });
      
      if (response.data.success) {
        const newPurchases = response.data.purchases || [];
        setMyPurchases(prevPurchases => {
          // If we were polling and a new purchase appeared, stop polling
          if (pollingPurchases && newPurchases.length > prevPurchases.length) {
            setPollingPurchases(false);
            showSnackbar('Payment confirmed! Your template is ready to download.', 'success');
          }
          return newPurchases;
        });
      }
    } catch (error) {
      console.error('Error fetching purchases:', error);
    }
  }, [isAuthenticated, getAuthHeaders, pollingPurchases, showSnackbar]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchMyPurchases();
    }
  }, [activeTab, fetchMyPurchases, isAuthenticated]);

  // Poll for purchases after payment initiation
  useEffect(() => {
    if (!pollingPurchases || !isAuthenticated) {
      return;
    }

    const pollInterval = setInterval(async () => {
      await fetchMyPurchases();
    }, 3000); // Poll every 3 seconds

    // Stop polling after 60 seconds (20 attempts)
    const timeout = setTimeout(() => {
      setPollingPurchases(false);
    }, 60000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [pollingPurchases, isAuthenticated, fetchMyPurchases]);

  const handlePurchase = (template) => {
    if (!isAuthenticated) {
      showSnackbar('Please log in to purchase templates', 'warning');
      // Store the return tab (templates page = tab 3) in localStorage
      localStorage.setItem('returnToTab', '3');
      if (onTabChange) {
        onTabChange(1); // Navigate to Dashboard/Login tab
      }
      return;
    }
    setSelectedTemplate(template);
    setPurchaseDialogOpen(true);
  };

  const handlePayment = async () => {
    if (!phoneNumber.trim()) {
      showSnackbar('Please enter your phone number', 'error');
      return;
    }

    try {
      setPaymentLoading(true);
      const formData = new FormData();
      formData.append('template_id', selectedTemplate.id);
      formData.append('phone_number', phoneNumber);

      const authHeaders = await getAuthHeaders();
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';

      const response = await axios.post(`${API_URL}/templates/purchase/initiate`, formData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        if (response.data.already_purchased) {
          showSnackbar('You have already purchased this template', 'info');
        } else {
          showSnackbar('Payment request sent! Complete payment on your phone. Waiting for confirmation...', 'success');
          // Start polling for purchase confirmation
          setPollingPurchases(true);
          // Switch to "My Purchases" tab after a short delay to guide the user
          setTimeout(() => {
            setActiveTab(1);
          }, 2000);
        }
        setPurchaseDialogOpen(false);
        setPhoneNumber('');
        fetchMyPurchases();
      } else {
        showSnackbar('Payment initiation failed', 'error');
      }
    } catch (error) {
      console.error('Payment error:', error);
      showSnackbar(error.response?.data?.detail || 'Payment failed', 'error');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDownload = async (template) => {
    try {
      const authHeaders = await getAuthHeaders();
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';

      const response = await axios.get(`${API_URL}/templates/${template.id}/download`, {
        headers: authHeaders
      });

      if (response.data.success) {
        // Open download URL in new tab
        window.open(response.data.download_url, '_blank');
        showSnackbar('Download started', 'success');
      }
    } catch (error) {
      console.error('Download error:', error);
      if (error.response?.status === 403) {
        showSnackbar('You have not purchased this template', 'error');
      } else {
        showSnackbar('Download failed', 'error');
      }
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(price);
  };

  const filteredTemplates = templates.filter(template => {
    if (searchQuery.trim() === '') return true;
    const query = searchQuery.toLowerCase();
    return (
      template.title?.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query) ||
      template.category?.toLowerCase().includes(query)
    );
  });

  const purchasedTemplateIds = new Set(myPurchases.map(p => p.template_id || p.excel_templates?.id));

  return (
    <Box sx={{ minHeight: '100vh', background: '#f7fafc', py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600, color: '#1a202c' }}>
            Excel Budget Templates
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Customizable Excel budget templates for personal and business use
          </Typography>
        </Box>

        <MobileAppCta compact />

        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} centered sx={{ mb: 4 }}>
          <Tab label="Browse Templates" />
          <Tab label="My Purchases" disabled={!isAuthenticated} />
        </Tabs>

        {activeTab === 0 && (
          <>
            {/* Search and Filter */}
            <Paper sx={{ p: 2, mb: 4 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {categories.map((cat) => (
                      <Chip
                        key={cat.value}
                        label={cat.label}
                        onClick={() => setSelectedCategory(cat.value)}
                        color={selectedCategory === cat.value ? 'primary' : 'default'}
                        variant={selectedCategory === cat.value ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Templates Grid */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : filteredTemplates.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No templates found
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {filteredTemplates.map((template) => {
                  const isPurchased = purchasedTemplateIds.has(template.id);
                  
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={template.id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                          }
                        }}
                      >
                        {template.preview_image_url && (
                          <CardMedia
                            component="img"
                            height="200"
                            image={template.preview_image_url}
                            alt={template.title}
                            sx={{ objectFit: 'cover' }}
                          />
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                              {template.title}
                            </Typography>
                            {isPurchased && (
                              <Chip
                                icon={<CheckCircle />}
                                label="Owned"
                                color="success"
                                size="small"
                              />
                            )}
                          </Box>
                          <Chip
                            label={template.category || 'Uncategorized'}
                            size="small"
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                            {template.description || 'Professional budget template'}
                          </Typography>
                          <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                            {formatPrice(template.price)}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0 }}>
                          {isPurchased ? (
                            <Button
                              fullWidth
                              variant="contained"
                              startIcon={<Download />}
                              onClick={() => handleDownload(template)}
                            >
                              Download
                            </Button>
                          ) : (
                            <Button
                              fullWidth
                              variant="contained"
                              startIcon={<ShoppingCart />}
                              onClick={() => handlePurchase(template)}
                            >
                              Purchase
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        {activeTab === 1 && (
          <>
            {!isAuthenticated ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Please log in to view your purchases
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => onTabChange && onTabChange(1)}
                  sx={{ mt: 2 }}
                >
                  Go to Login
                </Button>
              </Paper>
            ) : (
              <>
                {pollingPurchases && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    icon={<CircularProgress size={20} />}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      ⏳ Waiting for payment confirmation...
                    </Typography>
                    <Typography variant="body2">
                      Please complete the payment on your phone. We're checking for your purchase automatically.
                    </Typography>
                  </Alert>
                )}
                <Alert 
                  severity="success" 
                  sx={{ mb: 3 }}
                  icon={<Download />}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    📥 After completing payment, your template will appear here!
                  </Typography>
                  <Typography variant="body2">
                    Click the "Download" button on any purchased template to get your Excel file immediately.
                  </Typography>
                </Alert>
                {myPurchases.length === 0 ? (
                  <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="text.secondary">
                      You haven't purchased any templates yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Complete a payment to see your purchased templates here
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={3}>
                    {myPurchases.map((purchase) => {
                      const template = purchase.excel_templates || purchase;
                      return (
                        <Grid item xs={12} sm={6} md={4} key={purchase.id}>
                          <Card>
                            {template.preview_image_url && (
                              <CardMedia
                                component="img"
                                height="200"
                                image={template.preview_image_url}
                                alt={template.title}
                              />
                            )}
                            <CardContent>
                              <Typography variant="h6" gutterBottom>
                                {template.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Purchased: {new Date(purchase.purchased_at).toLocaleDateString()}
                              </Typography>
                              {purchase.mpesa_receipt_number && (
                                <Typography variant="caption" color="text.secondary">
                                  Receipt: {purchase.mpesa_receipt_number}
                                </Typography>
                              )}
                            </CardContent>
                            <CardActions>
                              <Button
                                fullWidth
                                variant="contained"
                                startIcon={<Download />}
                                onClick={() => handleDownload(template)}
                              >
                                Download
                              </Button>
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </>
            )}
          </>
        )}

        {/* Purchase Dialog */}
        <Dialog open={purchaseDialogOpen} onClose={() => setPurchaseDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Purchase Template</Typography>
              <IconButton onClick={() => setPurchaseDialogOpen(false)} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedTemplate && (
              <>
                <Typography variant="h6" gutterBottom>
                  {selectedTemplate.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedTemplate.description}
                </Typography>
                <Box sx={{ my: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Price:
                  </Typography>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
                    {formatPrice(selectedTemplate.price)}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  label="Phone Number"
                  placeholder="254XXXXXXXXX or 0XXXXXXXXX"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  sx={{ mt: 2 }}
                  InputProps={{
                    startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  helperText="Enter your M-Pesa registered phone number"
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 500, mb: 1 }}>
                    You will receive an STK push notification on your phone to complete the payment.
                  </Typography>
                  <Typography variant="body2" component="div" sx={{ mt: 1.5, fontWeight: 600, color: 'primary.main' }}>
                    📥 After completing payment, go to "My Purchases" tab to download your template immediately!
                  </Typography>
                </Alert>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPurchaseDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={paymentLoading || !phoneNumber.trim()}
              startIcon={paymentLoading ? <CircularProgress size={20} /> : <Payment />}
            >
              {paymentLoading ? 'Processing...' : 'Pay with M-Pesa'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ExcelTemplates;

